import { ExternalReaderApiClient } from './external-reader-api-client';
import { ReaderHttpCommunication } from './reader-http-communication';
import { GetByIdentityRequest } from '../requests/get-by-identity-request';
import { Observable } from 'rxjs';
import { GetByIdentityResponse } from '../responses/get-by-identity-response';
import { AutoCompleteModelRequest } from '../requests/auto-complete-model-request';
import { FindResponse } from '../responses/find-response';
import { GetByIdentityCachedResponse } from '../responses/get-by-identity-cached-response';
import { Injectable } from '@angular/core';
import type { CacheOptionsInterface, GetByIdentityParamsInterface, GetByIdentityResponseInterface, ModelInterface, ReaderApiClientInterface } from '@nts/std/interfaces';
import type { IdentityInterface } from '@nts/std/interfaces';

@Injectable()
export class ReaderApiClient<
    TModel extends ModelInterface<TIdentity>,
    TIdentity extends IdentityInterface,
> extends ExternalReaderApiClient<TModel, TIdentity> implements ReaderApiClientInterface<TIdentity, TModel> {

    protected createReaderHttpCommunication(): ReaderHttpCommunication {
        return new ReaderHttpCommunication(this.agent);
    }

    getByIdentity({
        request,
        modelType,
        responseInstance = new GetByIdentityResponse<TModel, TIdentity>(modelType),
        cacheOptions = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        }
    }: GetByIdentityParamsInterface<TIdentity, TModel>): Observable<GetByIdentityResponseInterface<TModel, TIdentity>> {
        const communication = this.createReaderHttpCommunication();
        return communication.getByIdentity<TIdentity, TModel>({
            request,
            modelType,
            responseInstance,
            cacheOptions
        });
    }

    getByIdentityCached(
        request: GetByIdentityRequest<TIdentity>,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
    ): Observable<GetByIdentityCachedResponse<TModel, TIdentity>> {

        // Default for getByIdentityCached
        cacheOptions = {
            bypass: false,
            expirationTime: undefined,
            force: false,
            enterpriseBarrier: true,
            tenantBarrier: true,
            userBarrier: true,
            ...cacheOptions
        };

        const communication = this.createReaderHttpCommunication();
        return communication.getByIdentityCached<TIdentity, TModel>(
            request,
            this.rootModelType,
            cacheOptions
        );
    }

    autoCompleteModel(
        request: AutoCompleteModelRequest,
        cacheOptions: CacheOptionsInterface = {
            bypass: false,
            expirationTime: undefined,
            force: false,
        },
    ): Observable<FindResponse<TModel, TIdentity>> {
        const communication = this.createReaderHttpCommunication();
        return communication.autoCompleteModel<TModel, TIdentity>(
            request, this.rootModelType, cacheOptions
        );
    }
}
